import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Container, Table } from 'reactstrap';
import { toast } from 'react-toastify';

import AppBadge from 'components/App/AppBadge';
import AppLoading from 'components/App/Loading';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import FlightStatusBadge from 'components/App/FlightStatusBadge';
import Meta from 'components/App/Meta';
import ModalFlightNote from 'components/Modals/ModalFlightNote';
import ModalFlightRefund from 'components/Modals/ModalFlightRefund';
import ModalRequestAction from 'components/Modals/ModalRequestAction';
import RefundLedger from 'components/RefundLedger';
import images from 'assets/images';
import flightBookingApi from 'api/flightBookingApi';
import ENDPOINT from 'constants/endPoint';
import {
  EXCHANGE_STATUS_LABELS, EXTRA_KIND_LABELS,
  SCHEDULE_CHANGE_RESOLUTION_LABELS, SCHEDULE_CHANGE_SOURCE_LABELS, SEAT_CHANGE_STATUS_LABELS,
  TRIP_TYPE_LABELS,
  formatFlightAmount, formatFlightDateTime, formatFlightExtra, formatFlightSeat,
  formatFlightTicket, formatFlightTimeDate, formatLeg, formatPaymentRefund, formatRefundSource,
  formatScheduleChangeLeg, formatSeatMove,
  getExchangeRefundOwed, getLoadErrorText, getReasonFlags, handleFlightApiError,
  hasDroppedExtra, hasDroppedSeat, isScheduleChangeResolvable, showHtmlInTab,
} from 'constants/flightBooking';
import { convertIsoDateToString, showToastError } from 'utils';

import styles from './ManagerFlightBookingDetail.module.scss';

const PAGE_TITLE = 'Chi tiết đơn vé máy bay';

const breadcrumbList = [
  { name: 'Quản lý đặt vé máy bay', isActive: false, link: ENDPOINT.MANAGER_FLIGHT_BOOKING },
  { name: PAGE_TITLE, isActive: true, link: '' },
];

const ACTION = {
  CANCEL: 'cancel',
  VOID: 'void',
  RETRY_ISSUE: 'retry-issue',
  SYNC: 'sync',
  REFUND_CALCULATE: 'refund/calculate',
  REFUND_CONFIRM: 'refund/confirm',
  REQUEST_REFUND: 'refund/request',
  MARK_REFUNDED: 'mark-refunded',
  RESOLVE_DUPLICATE: 'resolve-duplicate-payment',
  RESOLVE_AMOUNT_MISMATCH: 'resolve-amount-mismatch',
  RESOLVE_SEAT_CHANGE: 'resolve-seat-change',
  MARK_EXCHANGE_REFUNDED: 'mark-exchange-refunded',
  RESOLVE_SCHEDULE_CHANGE: 'resolve-schedule-change',
  RESOLVE_EXTRAS: 'resolve-extras',
  ETICKET: 'eticket',
};

// Cancel copy by payment status: a paid ISSUE_FAILED order is cancelled into a customer refund.
const CANCEL_COPY = {
  UNPAID: {
    label: 'Huỷ giữ chỗ',
    confirm: 'Huỷ giữ chỗ (PNR) với đối tác cho đơn này?',
    success: 'Đã huỷ giữ chỗ',
  },
  PAID: {
    label: 'Huỷ đơn',
    confirm: 'Huỷ đơn với đối tác? Đơn đã thanh toán sẽ chuyển sang chờ hoàn tiền cho khách, hoàn tiền thủ công qua OnePay rồi đánh dấu đã hoàn tiền.',
    success: 'Đã huỷ đơn, chờ hoàn tiền cho khách',
  },
};

const SUCCESS_MESSAGE = {
  [ACTION.VOID]: 'Đã huỷ vé (void)',
  [ACTION.RETRY_ISSUE]: 'Đã gửi yêu cầu xuất vé lại',
  [ACTION.SYNC]: 'Đã cập nhật trạng thái từ hãng',
  [ACTION.REFUND_CONFIRM]: 'Đã hoàn vé với đối tác',
  [ACTION.REQUEST_REFUND]: 'Đã ghi nhận yêu cầu hoàn tiền',
  [ACTION.MARK_REFUNDED]: 'Đã đánh dấu hoàn tiền',
  [ACTION.RESOLVE_DUPLICATE]: 'Đã xử lý thanh toán trùng',
  [ACTION.RESOLVE_AMOUNT_MISMATCH]: 'Đã xử lý lệch tiền thanh toán',
  [ACTION.RESOLVE_SEAT_CHANGE]: 'Đã xử lý đổi ghế lỗi',
  [ACTION.MARK_EXCHANGE_REFUNDED]: 'Đã đánh dấu hoàn tiền đổi vé',
  [ACTION.RESOLVE_SCHEDULE_CHANGE]: 'Đã xử lý đổi lịch bay',
  [ACTION.RESOLVE_EXTRAS]: 'Đã xử lý mua thêm dịch vụ lỗi',
};

const CONFIRM_TEXT = {
  [ACTION.VOID]: 'Huỷ toàn bộ vé vừa xuất của đơn này với hãng (void)? Đơn đã thanh toán sẽ chuyển sang chờ hoàn tiền cho khách.',
  [ACTION.RETRY_ISSUE]: 'Gửi lại yêu cầu xuất vé cho đơn này?',
};

const NOTE_TITLE = {
  [ACTION.REQUEST_REFUND]: 'Yêu cầu hoàn thủ công',
  [ACTION.MARK_REFUNDED]: 'Đánh dấu đã hoàn tiền cho khách',
  [ACTION.RESOLVE_DUPLICATE]: 'Xử lý thanh toán trùng',
  [ACTION.RESOLVE_AMOUNT_MISMATCH]: 'Xử lý lệch tiền thanh toán',
  [ACTION.RESOLVE_SEAT_CHANGE]: 'Xử lý đổi ghế lỗi',
  [ACTION.MARK_EXCHANGE_REFUNDED]: 'Đánh dấu đã hoàn tiền đổi vé',
  [ACTION.RESOLVE_SCHEDULE_CHANGE]: 'Xử lý đổi lịch bay',
  [ACTION.RESOLVE_EXTRAS]: 'Xử lý mua thêm dịch vụ lỗi',
};

// api-contract.md, resolve-*: the amount-mismatch refund happens later through mark-refunded,
// so only the seat-change refund names its gateway reference here.
const NOTE_CHOICES = {
  [ACTION.RESOLVE_AMOUNT_MISMATCH]: [
    { value: 'ISSUE', label: 'Đã thu đủ phần chênh lệch, xuất vé' },
    { value: 'REFUND', label: 'Hoàn tiền cho khách, huỷ đơn' },
  ],
  [ACTION.RESOLVE_SEAT_CHANGE]: [
    { value: 'RETRY', label: 'Thử lại với hãng (theo phí ghế hiện tại)' },
    { value: 'REFUND', label: 'Đã hoàn phí đổi ghế cho khách', needsGatewayRef: true },
  ],
  [ACTION.RESOLVE_SCHEDULE_CHANGE]: [
    { value: 'RESEATED', label: 'Đã xếp lại chỗ cho khách' },
    { value: 'REFUNDED', label: 'Đã hoàn phí ghế', needsGatewayRef: true },
    { value: 'NO_ACTION', label: 'Không cần xử lý' },
  ],
  [ACTION.RESOLVE_EXTRAS]: [
    { value: 'RETRY', label: 'Thử lại với hãng (theo giá hiện tại)' },
    { value: 'REFUND', label: 'Đã hoàn phí mua dịch vụ cho khách', needsGatewayRef: true },
  ],
};

// Reused for the extras-purchase resolution too: both wordings are generic enough (retried with
// the airline / refunded the fee), no seat-specific text.
const SEAT_CHANGE_RESOLUTION = { RETRY: 'Đã thử lại với hãng', REFUND: 'Đã hoàn phí cho khách' };
// PAYMENT: what the customer paid for a failed exchange; AIRLINE: the airline's refundDue.
const EXCHANGE_REFUND_KIND = { PAYMENT: 'tiền đổi vé khách đã trả', AIRLINE: 'tiền hãng trả lại' };

// The booking may have moved on, or a failed partner step left a new state to show.
// PAYMENT_UNAVAILABLE only happens on the five OnePay refund actions below (the others fail
// against the airline, not OnePay), and always leaves a REQUESTED ledger entry to show.
const RELOAD_ON_ERROR = ['FLIGHT_ACTION_NOT_ALLOWED', 'PARTNER_UNAVAILABLE', 'PAYMENT_UNAVAILABLE'];

// onepay-refund.md §3.4: the five actions that can send a refund command straight to OnePay.
const REFUND_METHOD_ACTIONS = [
  ACTION.MARK_REFUNDED, ACTION.RESOLVE_DUPLICATE, ACTION.RESOLVE_SEAT_CHANGE,
  ACTION.RESOLVE_EXTRAS, ACTION.MARK_EXCHANGE_REFUNDED,
];

const ISSUE_UNCONFIRMED_HINT = 'Lệnh xuất vé không có phản hồi. Liên hệ HNH kiểm tra vé đã xuất chưa; không thử xuất lại.';

const VOID_HINT = 'Huỷ vé vừa xuất trong 24 giờ: hoàn gần đủ tiền, không mất phí hoàn của hãng. Hết hạn chỉ còn Hoàn vé theo điều kiện hãng.';
const SYNC_TITLE = 'Đọc lại đơn ở hãng (chỉ đọc), dùng khi trạng thái ở đây khác với hãng';

const PASSENGER_TYPE = { ADT: 'Người lớn', CHD: 'Trẻ em', INF: 'Em bé' };
const PAYMENT_STATUS = {
  UNPAID: 'Chưa thanh toán',
  PAID: 'Đã thanh toán',
  REFUND_PENDING: 'Chờ hoàn tiền',
  REFUNDED: 'Đã hoàn tiền',
};
const DOCUMENT_TYPE = { P: 'Hộ chiếu', I: 'CCCD' };
const FARE_RULE_SOURCE = { partner: 'theo đối tác', reference: 'tham khảo' };

// Partner failures carry a support code; show it instead of the partner message.
const showActionError = (error) => {
  const { message, trackingId, partnerErrorId } = error.response?.data || {};

  if (!trackingId) {
    handleFlightApiError(error);
    return;
  }

  const partnerCode = partnerErrorId ? `, mã lỗi đối tác ${partnerErrorId}` : '';
  showToastError(`${message || 'Có lỗi xảy ra'} (Mã hỗ trợ: ${trackingId}${partnerCode})`);
};

const formatPassenger = (passengers, index) => {
  if (!Number.isInteger(index)) return '-';
  const passenger = passengers.find((item) => item.index === index);
  const name = [passenger?.surname, passenger?.givenName].filter(Boolean).join(' ');
  return `#${index + 1}${name ? ` ${name}` : ''}`;
};

const formatPartnerError = (error) => (
  `${error.message} (Mã hỗ trợ: ${error.trackingId || '-'}, mã lỗi đối tác ${error.errorId || '-'})`
);

// One line per exchanged leg; a flight is a booking leg or {leg, segments}.
const formatExchangeFlights = (label, flights = [], tripType) => flights.map((flight) => (
  `${label}: ${formatLeg(flight.leg, tripType)} ${(flight.segments || []).map((segment) => [
    `${segment.marketingCarrier}${segment.flightNumber}`,
    `${segment.startPoint} ${formatFlightDateTime(segment.startAt)}`,
    '→',
    `${segment.endPoint} ${formatFlightDateTime(segment.endAt)}`,
  ].join(' ')).join(', ')}`
));

const sumPaidAttempts = (payment) => (payment.paidAttempts || [])
  .reduce((total, attempt) => total + (attempt.amount || 0), 0);

const renderInfo = (label, value) => (
  <div key={label}>
    <div className={styles.InfoLabel}>{label}</div>
    <div className={styles.InfoValue}>{value || value === 0 ? value : '-'}</div>
  </div>
);

const renderTable = (heads, rows) => (
  <div className="table-responsive">
    <Table size="sm" className={styles.Table}>
      <thead>
        <tr>{heads.map((head) => <th key={head}>{head}</th>)}</tr>
      </thead>
      <tbody>{rows}</tbody>
    </Table>
  </div>
);

const formatDocument = (document) => (
  document?.number ? `${DOCUMENT_TYPE[document.type] || document.type} ${document.number}` : '-'
);

// Partner baggage strings are inconsistent across airlines, so show the raw text.
const formatBaggage = (baggage) => (
  `Hành lý xách tay ${baggage?.raw?.carryOn || '-'}, ký gửi ${baggage?.raw?.checked || 'không'}`
);

function ManagerFlightBookingDetail() {
  const { id } = useParams();
  const { t } = useTranslation();

  const [booking, setBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState('');
  const [confirmAction, setConfirmAction] = useState('');
  const [noteAction, setNoteAction] = useState('');
  const [refundQuote, setRefundQuote] = useState(null);
  const [refundExchange, setRefundExchange] = useState(null);
  const [resolvingScheduleChange, setResolvingScheduleChange] = useState(null);
  const [loadError, setLoadError] = useState('');
  // Increments (never toggles) so RefundMethodField can re-force manual on a repeat rejection.
  const [forceManualOn, setForceManualOn] = useState(0);

  const loadBooking = useCallback(() => flightBookingApi.getFlightBookingDetail(id)
    .then((response) => setBooking(response.payload))
    .catch((error) => {
      const isNotFound = error.response?.status === 404
        || error.response?.data?.key === 'FLIGHT_BOOKING_NOT_FOUND';
      setLoadError(isNotFound
        ? 'Không tìm thấy đơn vé máy bay'
        : getLoadErrorText(error, 'đơn vé máy bay', 'Vui lòng tải lại trang.'));
      handleFlightApiError(error);
    })
    .finally(() => setIsLoading(false)), [id]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  const cancelCopy = CANCEL_COPY[booking?.paymentStatus] || CANCEL_COPY.UNPAID;
  const successMessage = { ...SUCCESS_MESSAGE, [ACTION.CANCEL]: cancelCopy.success };
  const confirmText = { ...CONFIRM_TEXT, [ACTION.CANCEL]: cancelCopy.confirm };

  // Returns the response payload, or undefined when the action failed.
  const runAction = async (action, body) => {
    setPendingAction(action);
    try {
      const response = await flightBookingApi.postFlightBookingAction(id, action, body);
      if (successMessage[action]) toast.success(successMessage[action]);
      return response.payload || {};
    } catch (error) {
      const errorKey = error.response?.data?.key;
      if (errorKey === 'REFUND_QUOTE_EXPIRED') {
        // Expire the quote so the refund modal offers "Tính lại".
        setRefundQuote((quote) => quote && { ...quote, quoteExpiresAt: new Date().toISOString() });
      }
      if (RELOAD_ON_ERROR.includes(errorKey)) loadBooking();
      if (REFUND_METHOD_ACTIONS.includes(action) && errorKey === 'REFUND_REJECTED') {
        const {
          onePayMessage, responseCode, approvedAmount, remainingAmount,
        } = error.response.data;
        const currency = booking?.pricing?.currency;
        showToastError(
          `OnePay từ chối lệnh hoàn (mã ${responseCode}): ${onePayMessage}. `
          + `Đã duyệt ${formatFlightAmount(approvedAmount, currency)}, `
          + `còn lại ${formatFlightAmount(remainingAmount, currency)}.`,
        );
        setForceManualOn((n) => n + 1);
        return undefined;
      }
      if (REFUND_METHOD_ACTIONS.includes(action) && errorKey === 'PAYMENT_UNAVAILABLE') {
        showToastError('OnePay không trả lời, bấm lại để kiểm tra');
        return undefined;
      }
      showActionError(error);
      return undefined;
    } finally {
      setPendingAction('');
    }
  };

  const applyAction = async (action, body) => {
    const result = await runAction(action, body);
    if (result) await loadBooking();
    return result;
  };

  const onConfirmSimpleAction = async () => {
    await applyAction(confirmAction);
    setConfirmAction('');
  };

  const onCalculateRefund = async () => {
    const quote = await runAction(ACTION.REFUND_CALCULATE);
    if (quote) setRefundQuote(quote);
  };

  const onConfirmRefund = async (quoteId) => {
    if (await applyAction(ACTION.REFUND_CONFIRM, { quoteId })) setRefundQuote(null);
  };

  // The newest failed seat change nobody resolved yet (seatChanges are newest first).
  // Only a paid failure needs the admin; an unpaid one is history.
  const openSeatChange = (booking?.seatChanges || [])
    .find((change) => change.status === 'FAILED' && change.paidAt && !change.resolution);

  // Same rule as openSeatChange (extraPurchases are newest first too).
  const openExtrasPurchase = (booking?.extraPurchases || [])
    .find((purchase) => purchase.status === 'FAILED' && purchase.paidAt && !purchase.resolution);

  const onConfirmNote = async (body) => {
    const target = {
      [ACTION.RESOLVE_SEAT_CHANGE]: { seatChangeId: openSeatChange?.id },
      [ACTION.MARK_EXCHANGE_REFUNDED]: { exchangeId: refundExchange?.id },
      [ACTION.RESOLVE_SCHEDULE_CHANGE]: { scheduleChangeId: resolvingScheduleChange?.id },
      [ACTION.RESOLVE_EXTRAS]: { extraPurchaseId: openExtrasPurchase?.id },
    }[noteAction];
    if (await applyAction(noteAction, { ...target, ...body })) {
      setNoteAction('');
      setForceManualOn(0);
    }
  };

  const onCloseNote = () => {
    setNoteAction('');
    setForceManualOn(0);
  };

  const onMarkExchangeRefunded = (exchange) => {
    setRefundExchange(exchange);
    setNoteAction(ACTION.MARK_EXCHANGE_REFUNDED);
  };

  const onResolveScheduleChange = (entry) => {
    setResolvingScheduleChange(entry);
    setNoteAction(ACTION.RESOLVE_SCHEDULE_CHANGE);
  };

  const getNoteDescription = () => {
    const {
      pricing = {}, payment = {}, passengers = [], legs = [], tripType,
    } = booking || {};
    const { currency } = pricing;
    if (noteAction === ACTION.RESOLVE_AMOUNT_MISMATCH) {
      return `Tổng khách phải trả ${formatFlightAmount(pricing.grandTotal, currency)}, `
        + `các lần thanh toán thành công cộng lại ${formatFlightAmount(sumPaidAttempts(payment), currency)}.`;
    }
    if (noteAction === ACTION.RESOLVE_SEAT_CHANGE && openSeatChange) {
      const moves = openSeatChange.seats
        .map((seat) => `${formatPassenger(passengers, seat.passengerIndex)} ${formatSeatMove(seat)}`)
        .join('; ');
      const paid = openSeatChange.paidAt
        ? `, khách đã trả lúc ${formatFlightDateTime(openSeatChange.paidAt)}`
        : '';
      const error = openSeatChange.error ? ` Lỗi hãng: ${openSeatChange.error.message}.` : '';
      return `Yêu cầu lúc ${formatFlightDateTime(openSeatChange.requestedAt)}: ${moves}. `
        + `Phí ${formatFlightAmount(openSeatChange.fee, currency)}${paid}.${error}`;
    }
    if (noteAction === ACTION.RESOLVE_EXTRAS && openExtrasPurchase) {
      const items = (openExtrasPurchase.items || [])
        .map((item) => formatFlightExtra(item, currency, legs.length > 1, tripType))
        .join('; ');
      const paid = openExtrasPurchase.paidAt
        ? `, khách đã trả lúc ${formatFlightDateTime(openExtrasPurchase.paidAt)}`
        : '';
      const error = openExtrasPurchase.error ? ` Lỗi hãng: ${openExtrasPurchase.error.message}.` : '';
      return `Yêu cầu lúc ${formatFlightDateTime(openExtrasPurchase.requestedAt)}: ${items}. `
        + `Phí ${formatFlightAmount(openExtrasPurchase.fee, currency)}${paid}.${error}`;
    }
    if (noteAction === ACTION.RESOLVE_SCHEDULE_CHANGE && resolvingScheduleChange) {
      const legsSummary = (resolvingScheduleChange.legs || [])
        .map(formatScheduleChangeLeg).join('; ');
      const seatsSummary = (resolvingScheduleChange.seatsLost || [])
        .map((seat) => `${formatPassenger(passengers, seat.passengerIndex)} ${seat.number} `
          + `(${formatFlightAmount(seat.price, currency)})`)
        .join('; ');
      return `Phát hiện lúc ${formatFlightTimeDate(resolvingScheduleChange.detectedAt)}: ${
        legsSummary}.${seatsSummary ? ` Ghế mất: ${seatsSummary}.` : ''}`;
    }
    const owed = noteAction === ACTION.MARK_EXCHANGE_REFUNDED
      && refundExchange && getExchangeRefundOwed(refundExchange);
    if (owed?.kind === 'PAYMENT') {
      return `Hoàn tiền đổi vé khách đã trả: ${formatFlightAmount(owed.amount, currency)} `
        + `(đổi vé thất bại, báo giá lúc ${formatFlightDateTime(refundExchange.quotedAt)}).`;
    }
    if (owed?.kind === 'AIRLINE') {
      return `Hoàn tiền hãng trả lại: ${formatFlightAmount(owed.amount, currency)} `
        + `(đổi vé lúc ${formatFlightDateTime(refundExchange.appliedAt)}).`;
    }
    return '';
  };

  const onViewETicket = async () => {
    // Opened on the click so popup blockers let it through; filled once the HTML arrives.
    const tab = window.open('', '_blank');
    if (!tab) {
      showToastError('Trình duyệt đã chặn cửa sổ mới, hãy cho phép popup rồi thử lại');
      return;
    }
    setPendingAction(ACTION.ETICKET);
    try {
      showHtmlInTab(tab, await flightBookingApi.getFlightBookingETicket(id));
    } catch (error) {
      tab.close();
      if (error.response?.data?.key === 'FLIGHT_ACTION_NOT_ALLOWED') loadBooking();
      showActionError(error);
    } finally {
      setPendingAction('');
    }
  };

  const renderActions = () => {
    const flags = booking.flags || {};
    const buttons = [
      flags.canViewETicket && {
        key: ACTION.ETICKET,
        label: 'Xem vé điện tử',
        outline: true,
        onClick: onViewETicket,
      },
      flags.canCancel && {
        key: ACTION.CANCEL,
        label: cancelCopy.label,
        onClick: () => setConfirmAction(ACTION.CANCEL),
      },
      flags.canVoid && {
        key: ACTION.VOID,
        label: flags.voidDeadline
          ? `Huỷ vé trong 24h (hạn ${convertIsoDateToString(flags.voidDeadline, 'DD/MM HH:mm')})`
          : 'Huỷ vé trong 24h',
        onClick: () => setConfirmAction(ACTION.VOID),
      },
      flags.canRefund && {
        key: ACTION.REFUND_CALCULATE,
        label: 'Hoàn vé',
        onClick: onCalculateRefund,
      },
      flags.canRequestRefund && {
        key: ACTION.REQUEST_REFUND,
        label: 'Yêu cầu hoàn thủ công',
        onClick: () => setNoteAction(ACTION.REQUEST_REFUND),
      },
      flags.canRetryIssue && {
        key: ACTION.RETRY_ISSUE,
        label: 'Xuất vé lại',
        onClick: () => setConfirmAction(ACTION.RETRY_ISSUE),
      },
      flags.canMarkRefunded && {
        key: ACTION.MARK_REFUNDED,
        label: 'Đánh dấu đã hoàn tiền',
        onClick: () => setNoteAction(ACTION.MARK_REFUNDED),
      },
      flags.canResolveDuplicatePayment && {
        key: ACTION.RESOLVE_DUPLICATE,
        label: 'Xử lý thanh toán trùng',
        onClick: () => setNoteAction(ACTION.RESOLVE_DUPLICATE),
      },
      flags.canResolveAmountMismatch && {
        key: ACTION.RESOLVE_AMOUNT_MISMATCH,
        label: 'Xử lý lệch tiền',
        onClick: () => setNoteAction(ACTION.RESOLVE_AMOUNT_MISMATCH),
      },
      flags.canResolveSeatChange && openSeatChange && {
        key: ACTION.RESOLVE_SEAT_CHANGE,
        label: 'Xử lý đổi ghế lỗi',
        onClick: () => setNoteAction(ACTION.RESOLVE_SEAT_CHANGE),
      },
      flags.canResolveExtras && openExtrasPurchase && {
        key: ACTION.RESOLVE_EXTRAS,
        label: 'Xử lý mua thêm dịch vụ lỗi',
        onClick: () => setNoteAction(ACTION.RESOLVE_EXTRAS),
      },
      flags.canSync && {
        key: ACTION.SYNC,
        label: 'Cập nhật trạng thái từ hãng',
        title: SYNC_TITLE,
        outline: true,
        onClick: () => applyAction(ACTION.SYNC),
      },
    ].filter(Boolean);

    return buttons.map((button) => (
      <Button
        key={button.key}
        outline={button.outline}
        title={button.title}
        loading={pendingAction === button.key}
        disabled={!!pendingAction}
        onClick={button.onClick}
      >
        {button.label}
      </Button>
    ));
  };

  const renderContent = () => {
    const {
      legs = [], passengers = [], pnrs = [], partnerCalls = [], seatChanges = [], exchanges = [],
      scheduleChanges = [], extraPurchases = [], fareRules, tripType, instant,
    } = booking;
    const { contact = {}, invoice, pricing = {}, payment = {}, refund, issuance } = booking;
    const { currency } = pricing;

    return (
      <>
        <Card className={styles.Card}>
          <div className={styles.Header}>
            <div>
              <h4 className={styles.OrderId}>{booking.orderId}</h4>
              <FlightStatusBadge status={booking.status} />
              {instant && <span className={styles.Flag}>Vé gấp</span>}
              {getReasonFlags(booking.needsActionReasons).map((label) => (
                <span key={label} className={styles.Flag}>{label}</span>
              ))}
              {/* Derived from seat status, not seatsDropped: the flag must outlive the payment. */}
              {hasDroppedSeat(passengers) && (
                <span className={styles.Flag}>Có ghế không giữ được</span>
              )}
              {/* Same reasoning as the seat flag: derived from status, not extrasDropped. */}
              {hasDroppedExtra(passengers) && (
                <span className={styles.Flag}>Có dịch vụ không giữ được</span>
              )}
              {/* The pill cannot say what to do, and the wrong move here costs a second ticket. */}
              {(booking.needsActionReasons || []).includes('ISSUE_UNCONFIRMED') && (
                <div className={styles.Hint}>{ISSUE_UNCONFIRMED_HINT}</div>
              )}
            </div>
            <div>
              <div className={styles.Actions}>{renderActions()}</div>
              {booking.flags?.canVoid && <div className={styles.Hint}>{VOID_HINT}</div>}
            </div>
          </div>
          <div className={styles.InfoGrid}>
            {renderInfo('Trạng thái đối tác', booking.partnerStatus)}
            {renderInfo('Thanh toán', PAYMENT_STATUS[booking.paymentStatus] || booking.paymentStatus)}
            {renderInfo('Hệ thống', booking.system)}
            {renderInfo('Loại hành trình', TRIP_TYPE_LABELS[tripType] || tripType)}
            {renderInfo('PNR', pnrs
              .map((pnr) => `${pnr.bookingCode} (${pnr.partnerStatus || '-'})`)
              .join(', '))}
            {renderInfo('Hạn thanh toán', formatFlightDateTime(booking.paymentDeadline))}
            {renderInfo('Hạn xuất vé đối tác', formatFlightDateTime(booking.partnerTimePurchase))}
            {renderInfo('Hết hạn giữ chỗ đối tác', formatFlightDateTime(booking.partnerExpirationDate))}
            {renderInfo('Ngày tạo', formatFlightDateTime(booking.createdAt))}
            {/* phase-2-remaining.md §4: the hold-extras rebook pair, null on every other order. */}
            {booking.rebookedAs && renderInfo(
              'Đã đặt lại thành',
              <Link to={ENDPOINT.MANAGER_FLIGHT_BOOKING_DETAIL(booking.rebookedAs.id)}>
                {booking.rebookedAs.orderId}
              </Link>,
            )}
            {booking.rebookedFrom && renderInfo(
              'Đặt lại từ',
              <Link to={ENDPOINT.MANAGER_FLIGHT_BOOKING_DETAIL(booking.rebookedFrom.id)}>
                {booking.rebookedFrom.orderId}
              </Link>,
            )}
            {issuance?.lastErrorId && renderInfo(
              'Lỗi xuất vé gần nhất',
              `${issuance.lastErrorId} (Mã hỗ trợ: ${issuance.lastTrackingId || '-'})`,
            )}
          </div>
        </Card>

        <Card className={styles.Card}>
          <h5 className={styles.Title}>Hành trình</h5>
          {legs.map((leg) => (
            <div className={styles.Leg} key={leg.leg}>
              <div className={styles.Strong}>
                {`${formatLeg(leg.leg, tripType)}: ${leg.startPoint} → ${leg.endPoint}`}
              </div>
              <div className={styles.Muted}>
                {`${formatFlightDateTime(leg.startAt)} - ${formatFlightDateTime(leg.endAt)}`}
                {` · Hạng vé ${leg.ticketClass?.fareClass || '-'} (${leg.ticketClass?.fareBasis || '-'})`}
                {` · ${formatBaggage(leg.ticketClass?.baggage)}`}
              </div>
              {renderTable(
                ['Chuyến bay', 'Khai thác', 'Máy bay', 'Khởi hành', 'Đến', 'Khoang'],
                (leg.segments || []).map((segment) => (
                  <tr key={`${segment.flightNumber}-${segment.startAt}`}>
                    <td>{`${segment.marketingCarrier}${segment.flightNumber}`}</td>
                    <td>{segment.operatingCarrier || '-'}</td>
                    <td>{segment.plane || '-'}</td>
                    <td>{`${segment.startPoint} ${formatFlightDateTime(segment.startAt)} ${segment.startTerminal || ''}`}</td>
                    <td>{`${segment.endPoint} ${formatFlightDateTime(segment.endAt)} ${segment.endTerminal || ''}`}</td>
                    <td>{segment.cabinCode || '-'}</td>
                  </tr>
                )),
              )}
            </div>
          ))}
        </Card>

        <Card className={styles.Card}>
          <h5 className={styles.Title}>Hành khách và vé</h5>
          {renderTable(
            ['#', 'Loại', 'Họ tên', 'Danh xưng', 'Ngày sinh', 'Giấy tờ', 'Vé', 'Ghế', 'Dịch vụ thêm'],
            passengers.map((passenger) => (
              <tr key={passenger.index}>
                <td>{passenger.index + 1}</td>
                <td>
                  {PASSENGER_TYPE[passenger.type] || passenger.type}
                  {Number.isInteger(passenger.parentId) && ` (đi cùng #${passenger.parentId + 1})`}
                </td>
                <td>{[passenger.surname, passenger.givenName].filter(Boolean).join(' ')}</td>
                <td>{passenger.title || '-'}</td>
                <td>{convertIsoDateToString(passenger.dateOfBirth) || '-'}</td>
                <td>{formatDocument(passenger.document)}</td>
                <td>
                  {(passenger.tickets || []).map((ticket) => (
                    <div key={ticket.ticketNumber}>
                      {formatFlightTicket(ticket)}
                    </div>
                  ))}
                </td>
                <td>
                  {(passenger.seats || []).length === 0 && '-'}
                  {(passenger.seats || []).map((seat) => (
                    <div key={seat.leg}>
                      {formatFlightSeat(seat, currency, legs.length > 1, tripType)}
                    </div>
                  ))}
                </td>
                <td>
                  {(passenger.extras || []).length === 0 && '-'}
                  {(passenger.extras || []).map((extra) => (
                    // At most one BAGGAGE/MEAL per leg, distinct codes for OTHER (ancillaries.md).
                    <div key={`${extra.leg}-${extra.kind}-${extra.code}`}>
                      {formatFlightExtra(extra, currency, legs.length > 1, tripType)}
                    </div>
                  ))}
                </td>
              </tr>
            )),
          )}
        </Card>

        {scheduleChanges.length > 0 && (
          <Card className={styles.Card}>
            <h5 className={styles.Title}>Lịch sử thay đổi chuyến bay</h5>
            {scheduleChanges.map((entry) => (
              <div className={styles.Leg} key={entry.id}>
                <div className={styles.Strong}>
                  {`Phát hiện lúc ${formatFlightTimeDate(entry.detectedAt)} · nguồn: `
                    + `${SCHEDULE_CHANGE_SOURCE_LABELS[entry.source] || entry.source}`}
                </div>
                {(entry.legs || []).map((leg) => (
                  <div className={styles.Muted} key={leg.leg}>
                    {formatScheduleChangeLeg(leg, tripType)}
                  </div>
                ))}
                {(entry.seatsLost || []).length > 0 && (
                  <div className="mt-2">
                    {`Ghế bị mất: ${entry.seatsLost.map((seat) => [
                      formatPassenger(passengers, seat.passengerIndex),
                      seat.number,
                      formatFlightAmount(seat.price, currency),
                    ].join(' · ')).join('; ')}`}
                  </div>
                )}
                <div className={`${styles.Muted} mt-2`}>
                  {entry.notifiedAt
                    ? `Đã gửi email khách lúc ${formatFlightDateTime(entry.notifiedAt)}`
                    : 'Chưa gửi email'}
                </div>
                {booking.flags?.canResolveScheduleChange && isScheduleChangeResolvable(entry) && (
                  <Button
                    classes="mt-2"
                    loading={pendingAction === ACTION.RESOLVE_SCHEDULE_CHANGE
                      && resolvingScheduleChange?.id === entry.id}
                    disabled={!!pendingAction}
                    onClick={() => onResolveScheduleChange(entry)}
                  >
                    Xử lý đổi lịch
                  </Button>
                )}
                {entry.resolution && (() => {
                  const { resolution, gatewayRef, resolvedAt, note } = entry.resolution;
                  const resolutionLabel = SCHEDULE_CHANGE_RESOLUTION_LABELS[resolution]
                    || resolution;
                  const gatewaySuffix = gatewayRef ? ` (mã tham chiếu ${gatewayRef})` : '';
                  return (
                    <div className={`${styles.Muted} mt-2`}>
                      {[
                        `Đã xử lý: ${resolutionLabel}${gatewaySuffix}`,
                        `lúc ${formatFlightDateTime(resolvedAt)}`,
                        note,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  );
                })()}
              </div>
            ))}
          </Card>
        )}

        {seatChanges.length > 0 && (
          <Card className={styles.Card}>
            <h5 className={styles.Title}>Lịch sử đổi ghế</h5>
            {renderTable(
              ['Yêu cầu lúc', 'Trạng thái', 'Ghế', 'Phí', 'Thanh toán lúc', 'Áp dụng lúc', 'Lỗi và xử lý'],
              seatChanges.map((change) => (
                <tr key={change.id}>
                  <td>{formatFlightDateTime(change.requestedAt)}</td>
                  <td>
                    {SEAT_CHANGE_STATUS_LABELS[change.status] || change.status}
                    {change.status === 'PENDING_PAYMENT' && change.expiresAt && (
                      <div className={styles.Muted}>{`hạn ${formatFlightDateTime(change.expiresAt)}`}</div>
                    )}
                  </td>
                  <td>
                    {(change.seats || []).map((seat) => (
                      <div key={`${seat.passengerIndex}-${seat.leg}`}>
                        {[
                          formatPassenger(passengers, seat.passengerIndex),
                          `${legs.length > 1 ? `${formatLeg(seat.leg, tripType)} ` : ''}${formatSeatMove(seat)}`,
                          formatFlightAmount(seat.price, currency),
                        ].join(' · ')}
                      </div>
                    ))}
                  </td>
                  <td>
                    {formatFlightAmount(change.fee, currency)}
                    {change.partnerFee !== null && change.partnerFee !== change.fee && (
                      <div className={styles.Muted}>
                        {`Hãng trừ: ${formatFlightAmount(change.partnerFee, currency)}`}
                      </div>
                    )}
                  </td>
                  <td>{formatFlightDateTime(change.paidAt)}</td>
                  <td>{formatFlightDateTime(change.appliedAt)}</td>
                  <td>
                    {change.error && <div>{formatPartnerError(change.error)}</div>}
                    {change.resolution && (
                      <div className={styles.Muted}>
                        {[
                          `${SEAT_CHANGE_RESOLUTION[change.resolution.resolution] || change.resolution.resolution} lúc ${formatFlightDateTime(change.resolution.resolvedAt)}`,
                          change.resolution.gatewayRef && `Mã tham chiếu ${change.resolution.gatewayRef}`,
                          change.resolution.note,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {!change.error && !change.resolution && '-'}
                  </td>
                </tr>
              )),
            )}
          </Card>
        )}

        {extraPurchases.length > 0 && (
          <Card className={styles.Card}>
            <h5 className={styles.Title}>Lịch sử mua thêm dịch vụ</h5>
            {renderTable(
              ['Yêu cầu lúc', 'Trạng thái', 'Dịch vụ', 'Phí', 'Thanh toán lúc', 'Áp dụng lúc', 'Lỗi và xử lý'],
              extraPurchases.map((purchase) => (
                <tr key={purchase.id}>
                  <td>{formatFlightDateTime(purchase.requestedAt)}</td>
                  <td>
                    {SEAT_CHANGE_STATUS_LABELS[purchase.status] || purchase.status}
                    {purchase.status === 'PENDING_PAYMENT' && purchase.expiresAt && (
                      <div className={styles.Muted}>{`hạn ${formatFlightDateTime(purchase.expiresAt)}`}</div>
                    )}
                  </td>
                  <td>
                    {(purchase.items || []).map((item) => (
                      <div key={`${item.leg}-${item.kind}-${item.code}`}>
                        {formatFlightExtra(item, currency, legs.length > 1, tripType)}
                      </div>
                    ))}
                    {purchase.partnerFee !== null && purchase.partnerFee !== purchase.fee && (
                      <div className={styles.Muted}>
                        {`Hãng trừ: ${formatFlightAmount(purchase.partnerFee, currency)}`}
                      </div>
                    )}
                  </td>
                  <td>{formatFlightAmount(purchase.fee, currency)}</td>
                  <td>{formatFlightDateTime(purchase.paidAt)}</td>
                  <td>{formatFlightDateTime(purchase.appliedAt)}</td>
                  <td>
                    {purchase.error && <div>{formatPartnerError(purchase.error)}</div>}
                    {purchase.resolution && (
                      <div className={styles.Muted}>
                        {[
                          `${SEAT_CHANGE_RESOLUTION[purchase.resolution.resolution] || purchase.resolution.resolution} lúc ${formatFlightDateTime(purchase.resolution.resolvedAt)}`,
                          purchase.resolution.gatewayRef && `Mã tham chiếu ${purchase.resolution.gatewayRef}`,
                          purchase.resolution.note,
                        ].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    {!purchase.error && !purchase.resolution && '-'}
                  </td>
                </tr>
              )),
            )}
          </Card>
        )}

        {exchanges.length > 0 && (
          <Card className={styles.Card}>
            <h5 className={styles.Title}>Lịch sử đổi vé</h5>
            {exchanges.map((exchange) => (
              <div className={styles.Leg} key={exchange.id}>
                <div className={styles.Strong}>
                  {`Báo giá lúc ${formatFlightDateTime(exchange.quotedAt)} · `
                    + `${EXCHANGE_STATUS_LABELS[exchange.status] || exchange.status}`}
                  {['QUOTED', 'PENDING_PAYMENT'].includes(exchange.status)
                    && ` (hạn ${formatFlightDateTime(exchange.expiresAt)})`}
                </div>
                {[
                  ...formatExchangeFlights('Chuyến cũ', exchange.oldFlights, tripType),
                  ...formatExchangeFlights('Chuyến mới', exchange.newFlights, tripType),
                ].map((line) => <div className={styles.Muted} key={line}>{line}</div>)}
                {renderTable(
                  ['Số vé', 'Hành khách', 'Giá cũ', 'Giá mới', 'Chênh lệch', 'Phí đổi', 'Hãng hoàn'],
                  (exchange.perTicket || []).map((ticket) => (
                    <tr key={ticket.ticketNumber}>
                      <td>{ticket.ticketNumber}</td>
                      <td>{formatPassenger(passengers, ticket.passengerIndex)}</td>
                      <td>{formatFlightAmount(ticket.oldPrice, currency)}</td>
                      <td>{formatFlightAmount(ticket.newPrice, currency)}</td>
                      <td>{formatFlightAmount(ticket.difference, currency)}</td>
                      <td>{formatFlightAmount(ticket.penalty, currency)}</td>
                      <td>{formatFlightAmount(ticket.refund, currency)}</td>
                    </tr>
                  )),
                )}
                <div className={`${styles.InfoGrid} mt-2`}>
                  {renderInfo('Khách phải trả', formatFlightAmount(exchange.amountDue, currency))}
                  {renderInfo('Hãng hoàn cho khách', formatFlightAmount(exchange.refundDue, currency))}
                  {renderInfo('Thanh toán lúc', formatFlightDateTime(exchange.paidAt))}
                  {renderInfo('Đổi vé lúc', formatFlightDateTime(exchange.appliedAt))}
                </div>
                {(exchange.seatsLost || []).length > 0 && (
                  <div className="mt-2">
                    {`Ghế bị mất: ${exchange.seatsLost.map((seat) => [
                      `${formatPassenger(passengers, seat.passengerIndex)} ${seat.number}`,
                      seat.price !== undefined && seat.price !== null
                        && `(${formatFlightAmount(seat.price, currency)})`,
                    ].filter(Boolean).join(' ')).join('; ')}`}
                  </div>
                )}
                {(exchange.extrasLost || []).length > 0 && (
                  <div className="mt-2">
                    {`Dịch vụ bị mất: ${exchange.extrasLost.map((extra) => [
                      `${formatPassenger(passengers, extra.passengerIndex)} `
                        + `${EXTRA_KIND_LABELS[extra.kind] || extra.kind}: ${extra.name}`,
                      extra.price !== undefined && extra.price !== null
                        && `(${formatFlightAmount(extra.price, currency)})`,
                    ].filter(Boolean).join(' ')).join('; ')}`}
                  </div>
                )}
                {exchange.error && <div className="mt-2">{formatPartnerError(exchange.error)}</div>}
                {booking.flags?.canMarkExchangeRefunded && getExchangeRefundOwed(exchange) && (
                  <Button
                    classes="mt-2"
                    loading={pendingAction === ACTION.MARK_EXCHANGE_REFUNDED
                      && refundExchange?.id === exchange.id}
                    disabled={!!pendingAction}
                    onClick={() => onMarkExchangeRefunded(exchange)}
                  >
                    Đánh dấu đã hoàn tiền
                  </Button>
                )}
                {exchange.refundedAt && (
                  <div className={`${styles.Muted} mt-2`}>
                    {[
                      `Đã hoàn ${EXCHANGE_REFUND_KIND[exchange.refundKind] || 'tiền'} lúc ${formatFlightDateTime(exchange.refundedAt)}`,
                      exchange.refundReference && `Mã tham chiếu ${exchange.refundReference}`,
                      exchange.refundNote,
                    ].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        <Card className={styles.Card}>
          <h5 className={styles.Title}>Liên hệ và hoá đơn</h5>
          <div className={styles.InfoGrid}>
            {renderInfo('Người liên hệ', contact.name)}
            {renderInfo('Số điện thoại', contact.phone)}
            {renderInfo('Email', contact.email && (
              <span className="d-inline-flex align-items-center">
                {contact.email}
                {/* emailVerified === false only; undefined/null is a legacy, verified account. */}
                {booking.user?.emailVerified === false && (
                  <AppBadge color="warning" className="ms-2">
                    {t('global.email_not_verified')}
                  </AppBadge>
                )}
              </span>
            ))}
            {invoice && renderInfo('Công ty', invoice.companyName)}
            {invoice && renderInfo('Mã số thuế', invoice.taxCode)}
            {invoice && renderInfo('Địa chỉ công ty', invoice.address)}
          </div>
        </Card>

        <Card className={styles.Card}>
          <h5 className={styles.Title}>Giá</h5>
          {renderTable(
            ['Loại khách', 'Số khách', 'Giá vé/khách', 'Thuế/khách', 'Giảm/khách', 'Phí DV/khách', 'Tổng/khách'],
            Object.entries(pricing.byType || {})
              .filter(([, row]) => row?.count)
              .map(([type, row]) => (
                <tr key={type}>
                  <td>{PASSENGER_TYPE[type] || type}</td>
                  <td>{row.count}</td>
                  <td>{formatFlightAmount(row.fare, currency)}</td>
                  <td>{formatFlightAmount(row.tax, currency)}</td>
                  <td>{formatFlightAmount(row.discount, currency)}</td>
                  <td>{formatFlightAmount(row.serviceFee, currency)}</td>
                  <td>{formatFlightAmount(row.total, currency)}</td>
                </tr>
              )),
          )}
          <div className={`${styles.InfoGrid} mt-3`}>
            {renderInfo('Phí dịch vụ', formatFlightAmount(pricing.serviceFee, currency))}
            {pricing.seatTotal > 0
              && renderInfo('Phí chọn ghế', formatFlightAmount(pricing.seatTotal, currency))}
            {renderInfo('Tổng khách trả', formatFlightAmount(pricing.grandTotal, currency))}
            {renderInfo('Tổng tiền đối tác', formatFlightAmount(pricing.partnerTotal, currency))}
          </div>
        </Card>

        <Card className={styles.Card}>
          <h5 className={styles.Title}>Thanh toán</h5>
          <div className={styles.InfoGrid}>
            {renderInfo('Cổng thanh toán', payment.gateway)}
            {renderInfo('Mã giao dịch (transactionNo)', payment.transactionNo)}
            {renderInfo('merchTxnRef', payment.merchTxnRef)}
            {renderInfo('Số tiền đã trả', formatFlightAmount(payment.paidAmount, currency))}
            {renderInfo('Thời gian trả', formatFlightDateTime(payment.paidAt))}
          </div>
          {(payment.duplicatePayment || payment.amountMismatch) && (
            <>
              <div className={`${styles.Strong} mt-3`}>Các lần thanh toán thành công</div>
              {renderTable(
                ['merchTxnRef', 'transactionNo', 'Số tiền', 'Thời gian'],
                (payment.paidAttempts || []).map((attempt) => (
                  <tr key={attempt.merchTxnRef}>
                    <td>{attempt.merchTxnRef}</td>
                    <td>{attempt.transactionNo || '-'}</td>
                    <td>{formatFlightAmount(attempt.amount, currency)}</td>
                    <td>{formatFlightDateTime(attempt.paidAt)}</td>
                  </tr>
                )),
              )}
            </>
          )}
          {/* A quote from "Hoàn vé" has no confirmedAt until it is confirmed. */}
          {(refund?.confirmedAt || refund?.refundedAt) && (
            <div className={`${styles.InfoGrid} mt-3`}>
              {renderInfo('Nguồn hoàn tiền', formatRefundSource(refund))}
              {renderInfo('Tổng hoàn từ đối tác', formatFlightAmount(refund.refundTotal, currency))}
              {renderInfo('Xác nhận hoàn với đối tác', formatFlightDateTime(refund.confirmedAt))}
              {renderInfo('Thời gian hoàn khách', formatFlightDateTime(refund.refundedAt))}
              {renderInfo('Mã tham chiếu hoàn tiền', refund.reference)}
              {renderInfo('Ghi chú', refund.note)}
            </div>
          )}
        </Card>

        {(payment.refunds || []).length > 0 && (
          <Card className={styles.Card}>
            <h5 className={styles.Title}>Lệnh hoàn OnePay</h5>
            <RefundLedger
              rows={payment.refunds.map((entry) => formatPaymentRefund(entry, currency))}
            />
          </Card>
        )}

        <Card className={styles.Card}>
          <h5 className={styles.Title}>
            {fareRules ? `Điều kiện vé (${FARE_RULE_SOURCE[fareRules.source] || fareRules.source})` : 'Điều kiện vé'}
          </h5>
          {fareRules?.rules?.length ? fareRules.rules.map((rule) => (
            <div className={styles.Leg} key={`${rule.passengerType}-${rule.route}-${rule.fareBasis}`}>
              <div className={styles.Strong}>
                {`${PASSENGER_TYPE[rule.passengerType] || rule.passengerType} · ${rule.route} · ${rule.fareBasis}`}
              </div>
              {(rule.sections || []).map((section) => (
                <details key={section.code}>
                  <summary>{section.title}</summary>
                  <pre className={styles.RuleText}>{section.text}</pre>
                </details>
              ))}
            </div>
          )) : <div className={styles.Muted}>Không có dữ liệu điều kiện vé</div>}
        </Card>

        <Card className={styles.Card}>
          <h5 className={styles.Title}>Nhật ký gọi đối tác</h5>
          {partnerCalls.length ? renderTable(
            ['Thời gian', 'Endpoint', 'Kết quả', 'HTTP', 'Mã lỗi', 'Mã hỗ trợ', 'Thời lượng'],
            partnerCalls.map((call) => (
              <tr key={`${call.trackingId}-${call.createdAt}`}>
                <td>{formatFlightDateTime(call.createdAt)}</td>
                <td>{call.endpoint}</td>
                <td>{call.ok ? 'Thành công' : 'Lỗi'}</td>
                <td>{call.httpStatus || '-'}</td>
                <td>{call.errorId || '-'}</td>
                <td>{call.trackingId || '-'}</td>
                <td>{call.durationMs !== undefined ? `${call.durationMs} ms` : '-'}</td>
              </tr>
            )),
          ) : <div className={styles.Muted}>Chưa có lượt gọi đối tác</div>}
        </Card>
      </>
    );
  };

  const renderBody = () => {
    if (isLoading) return <AppLoading loading />;
    if (!booking) {
      return <Card className={styles.Card}>{loadError || 'Không tìm thấy đơn vé máy bay'}</Card>;
    }
    return renderContent();
  };

  // Never more than what the customer paid (mirrors the BE cap on refund/request).
  const refundAmountField = noteAction === ACTION.REQUEST_REFUND ? {
    label: 'Số tiền hoàn (VND)',
    max: booking?.payment?.paidAmount || 0,
    hint: `Tối đa ${formatFlightAmount(booking?.payment?.paidAmount, booking?.pricing?.currency)} `
      + '(số tiền khách đã trả)',
  } : null;

  // The amount OnePay would be sent, informational only — BE computes the real figure and the
  // body never carries it (onepay-refund.md §3.4). One number per action, from data already on
  // the page: no separate call needed to show it.
  const refundMethodAmount = {
    [ACTION.MARK_REFUNDED]: booking?.refund?.refundTotal ?? booking?.payment?.paidAmount,
    [ACTION.RESOLVE_DUPLICATE]: (booking?.payment?.paidAttempts || [])
      .slice(1).reduce((sum, attempt) => sum + (attempt.amount || 0), 0),
    [ACTION.RESOLVE_SEAT_CHANGE]: openSeatChange?.fee,
    [ACTION.RESOLVE_EXTRAS]: openExtrasPurchase?.fee,
    [ACTION.MARK_EXCHANGE_REFUNDED]: refundExchange
      && getExchangeRefundOwed(refundExchange)?.amount,
  }[noteAction];

  const refundField = REFUND_METHOD_ACTIONS.includes(noteAction) ? {
    amountLabel: formatFlightAmount(refundMethodAmount, booking?.pricing?.currency),
    hasPendingRefund: !!booking?.flags?.hasPendingRefund,
    forceManualOn,
  } : null;

  return (
    <div className="page-content">
      <Meta name={PAGE_TITLE} />
      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />
        {renderBody()}
      </Container>

      <ModalRequestAction
        isOpen={!!confirmAction}
        icon={images.modalWarning.default}
        textRequest={confirmText[confirmAction] || ''}
        isLoadingConfirm={!!confirmAction && pendingAction === confirmAction}
        onCloseModal={() => setConfirmAction('')}
        onConfirm={onConfirmSimpleAction}
      />
      <ModalFlightNote
        isOpen={!!noteAction}
        title={NOTE_TITLE[noteAction] || ''}
        description={getNoteDescription()}
        choices={NOTE_CHOICES[noteAction]}
        amountField={refundAmountField}
        refundField={refundField}
        noGatewayRef={noteAction === ACTION.REQUEST_REFUND}
        isLoading={!!noteAction && pendingAction === noteAction}
        onConfirm={onConfirmNote}
        onClose={onCloseNote}
      />
      <ModalFlightRefund
        quote={refundQuote}
        isLoading={[ACTION.REFUND_CALCULATE, ACTION.REFUND_CONFIRM].includes(pendingAction)}
        onRecalculate={onCalculateRefund}
        onConfirm={onConfirmRefund}
        onClose={() => setRefundQuote(null)}
      />
    </div>
  );
}

export default ManagerFlightBookingDetail;
